import { useEffect, useRef } from 'react';

import type { DisplayCallEntry, DisplayLiveState } from './useDisplayLive';

/** The spoken phrase plus its BCP-47 language for one call announcement. */
export interface Utterance {
  text: string;
  lang: string;
}

/** Per-counter dedup key: same display code with a new called_at counts as
 *  a fresh call, so a recall re-announces. */
function callKey(call: DisplayCallEntry): string {
  return call.id;
}

type WindowWithWebkitAudio = typeof window & {
  webkitAudioContext?: typeof AudioContext;
};

function getAudioContextCtor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  const w = window as WindowWithWebkitAudio;
  return window.AudioContext ?? w.webkitAudioContext ?? null;
}

/** Plays a short two-tone chime through the shared AudioContext. Smart-TV
 *  browsers commonly support Web Audio even when they ship no TTS voices,
 *  so this is the audio path that should fire everywhere YouTube does. */
function playChime(ctx: AudioContext): void {
  const tones: Array<{ at: number; freq: number; dur: number }> = [
    { at: 0, freq: 880, dur: 0.18 },
    { at: 0.22, freq: 1320, dur: 0.28 },
  ];
  for (const { at, freq, dur } of tones) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const start = ctx.currentTime + at;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.35, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur + 0.05);
  }
}

/**
 * Plays a chime — and speaks the call when the browser has a TTS engine —
 * whenever a counter calls or recalls a token. Always on; the board has no
 * mute control. Smart-TV browsers often expose Web Audio but ship without
 * speech-synthesis voices, so the chime is the universal signal and TTS is
 * best-effort on top.
 *
 * Audio is unlocked on the first user gesture (browsers suspend new
 * AudioContexts until then). On TV-style autoplay browsers the unlock
 * usually succeeds on mount; elsewhere it lands on the first remote click.
 */
export function useCallAnnouncer(
  live: DisplayLiveState,
  build: (displayCode: string, counterCode: string) => Utterance,
): void {
  const ttsSupported =
    typeof window !== 'undefined' && 'speechSynthesis' in window;
  const audioCtorRef = useRef<typeof AudioContext | null>(null);
  audioCtorRef.current = getAudioContextCtor();
  const audioCtxRef = useRef<AudioContext | null>(null);
  // counterId -> the last call key seen at that counter; null for idle.
  const seen = useRef<Map<string, string | null>>(new Map());
  const buildRef = useRef(build);
  buildRef.current = build;

  // Lazily build the AudioContext and try to resume it. Returns null if the
  // browser has no Web Audio at all (we then silently skip the chime).
  const ensureAudio = (): AudioContext | null => {
    const Ctor = audioCtorRef.current;
    if (!Ctor) return null;
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new Ctor();
      } catch {
        return null;
      }
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    return ctx;
  };

  useEffect(() => {
    // Best-effort: try to build the context up front (TV browsers and
    // some kiosk setups allow autoplay without a gesture).
    ensureAudio();
    if (ttsSupported) {
      // Some engines need a warm-up speak before the first real utterance.
      const warm = new SpeechSynthesisUtterance(' ');
      warm.volume = 0;
      try {
        window.speechSynthesis.speak(warm);
      } catch {
        /* noop */
      }
    }
    const unlock = (): void => {
      ensureAudio();
    };
    document.addEventListener('pointerdown', unlock);
    document.addEventListener('keydown', unlock);
    return () => {
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
    };
    // ttsSupported is derived from window and stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // First-observation seed: any counter not yet in `seen` is primed with
    // its current state (call key if calling, null if idle). The initial
    // backlog is therefore recorded silently, and a future first call on a
    // counter that was idle at page load still announces.
    const callsByCounter = new Map(
      live.calls.map((call) => [call.counterId, call] as const),
    );
    for (const counterId of Object.keys(live.perCounter)) {
      if (!seen.current.has(counterId)) {
        const call = callsByCounter.get(counterId);
        seen.current.set(counterId, call ? callKey(call) : null);
      }
    }
    let chimed = false;
    for (const call of live.calls) {
      const key = callKey(call);
      const prev = seen.current.get(call.counterId);
      if (prev === key) continue;
      seen.current.set(call.counterId, key);
      // A counter first seen here is recorded but not announced.
      if (prev === undefined) continue;
      if (!chimed) {
        const ctx = ensureAudio();
        if (ctx) {
          try {
            playChime(ctx);
          } catch {
            /* ignore — the announcement still goes through TTS below */
          }
        }
        chimed = true;
      }
      if (ttsSupported) {
        const { text, lang } = buildRef.current(
          call.displayCode,
          call.counterCode,
        );
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = lang;
        try {
          window.speechSynthesis.speak(utterance);
        } catch {
          /* ignore — chime already played */
        }
      }
    }
  }, [live, ttsSupported]);
}
